import { useState } from 'react';
import { Lock, CheckCircle, EyeOff, Eye, ArrowLeft } from 'lucide-react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { authApi } from '../../api/auth';
import { toast } from 'sonner';

const resetPasswordSchema = z.object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(6, 'Please confirm your password')
}).refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"]
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const token = searchParams.get('invalid') ? null : searchParams.get('token');
    const [formData, setFormData] = useState<ResetPasswordFormData>({
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState<Partial<Record<keyof ResetPasswordFormData, string>>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState({
        password: false,
        confirmPassword: false
    });

    const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (errors[name as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const result = resetPasswordSchema.safeParse(formData);

        if (!result.success) {
            const fieldErrors: Record<string, string> = {};
            result.error.issues.forEach(issue => {
                if (issue.path.length > 0 && typeof issue.path[0] === 'string') {
                    fieldErrors[issue.path[0]] = issue.message;
                }
            });
            setErrors(fieldErrors);
            return;
        }

        if (!token) {
            toast.error('Invalid or missing reset token');
            return;
        }

        try {
            setIsLoading(true);
            const response = await authApi.resetPassword(token, formData.password, formData.confirmPassword);

            if (response.success) {
                setIsSubmitted(true);
                toast.success('Password has been reset successfully');
            } else {
                if (response.message?.toLowerCase().includes('expired') || response.message?.toLowerCase().includes('invalid')) {
                    return navigate('/reset-password?invalid=true');
                }
                toast.error(response.message || 'Failed to reset password');
            }
        } catch (error: any) {
            console.error('Reset password error:', error);
            toast.error(error.message || 'An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-[#020b12] flex items-center justify-center p-4 font-outfit relative overflow-hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px] pointer-events-none" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[128px] pointer-events-none" />

                <div className="bg-[#0B1623]/80 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative z-10">
                    <div className="p-8 sm:p-10">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-linear-to-br from-red-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-red-500/20">
                                <Lock className="w-6 h-6 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-white mb-2">Link Expired</h1>
                            <p className="text-slate-400">The reset link has expired or is invalid</p>
                        </div>

                        <div className="space-y-6">
                            <p className="text-slate-400 text-center">
                                The password reset link is no longer valid. Please request a new one to continue.
                            </p>

                            <Link
                                to="/forgot-password"
                                className="block w-full bg-linear-to-r from-cyan-600 to-blue-600 text-white font-semibold py-3.5 px-6 rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-900/20 text-center"
                            >
                                Request New Reset Link
                            </Link>

                            <div className="pt-4 border-t border-white/10 text-center">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                                >
                                    Back to Login
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#020b12] flex items-center justify-center p-4 font-outfit relative overflow-hidden">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[128px] pointer-events-none" />

            <div className="bg-[#0B1623]/80 backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden relative z-10">
                {!isSubmitted ? (
                    <div className="p-8 sm:p-10">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-linear-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-cyan-500/20">
                                <Lock className="w-6 h-6 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-white">Reset Password</h1>
                            <p className="text-slate-400 mt-2 text-sm">Enter your new password below.</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">
                                    New Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <input
                                        type={showPassword.password ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="w-full pl-12 pr-12 py-3.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-white placeholder:text-slate-600 transition-all"
                                        placeholder="••••••••"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => togglePasswordVisibility('password')}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                                    >
                                        {showPassword.password ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-red-400 text-xs ml-1 mt-1">{errors.password}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <div className="relative">
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                        <input
                                            type={showPassword.confirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="w-full pl-12 pr-12 py-3.5 bg-black/20 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 text-white placeholder:text-slate-600 transition-all"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('confirmPassword')}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors"
                                        >
                                            {showPassword.confirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    {errors.confirmPassword && (
                                        <p className="text-red-400 text-xs ml-1 mt-1">{errors.confirmPassword}</p>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full bg-linear-to-r from-cyan-600 to-blue-600 text-white font-semibold py-3.5 rounded-xl hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-900/20 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Resetting...
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                        </form>

                        <div className="mt-8 pt-6 border-t border-white/10 text-center">
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                            >
                                Back to Login
                            </Link>
                        </div>
                    </div>
                ) : (<>
                    <div className="text-center space-y-6 p-8">
                        {/* Success Icon */}
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-10 h-10 text-green-500" />
                        </div>

                        {/* Success Message */}
                        <div className="space-y-3">
                            <h3 className="text-2xl font-bold text-white">Password Reset Successful</h3>
                            <p className="text-slate-400 text-base">
                                Your password has been updated successfully. You can now log in with your new password.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-8 pt-6 border-t border-white/10 text-center">
                            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
                                <ArrowLeft className="w-4 h-4" />
                                Back to Login
                            </Link>
                        </div>
                    </div>
                </>
                )}
            </div>
        </div>
    );
}